import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "./schema";
import { useTranslation } from "react-i18next";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import MemoForm from "./components/MemoForm";
import BuyForm from "./components/BuyForm";
import HoldForm from "./components/HoldForm";
import CanRequest from "./components/CanRequest";
import { useIsFocused } from "@react-navigation/native";
import DynamicForm from "../DynamicForm/DynamicForm";
import { useTheme } from "styled-components/native";
import { useUserCurrency } from "../../../hooks/getCurrency";
import {
  calculateItemPrices,
  formatNumberWithPrecision,
} from "@utils/product/productFormatters";
import { addComma } from "@utils/product/productFormatters";
import { MemoStatus } from "@src/types/HistoryRequest";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { ProductCategory } from "@utils/constants";
import { isMeleeItem } from "@utils/product/productValidators";

export const getPaymentType = (type: string | undefined, t?: any) => {
  if (type == null) return undefined;
  const normalized = type.toLowerCase().trim();
  if (normalized === "invoice")
    return t
      ? t("requestHistoryDetail.paymentType.invoice.text", "Invoice")
      : "Invoice";
  if (normalized === "cod")
    return t ? t("requestHistoryDetail.paymentType.cod.text", "COD") : "COD";
  return type;
};

const BmhForm = (props: any) => {
  const {
    HeaderComponent,
    selectedOption,
    canRequestAgain,
    stockItemRequest,
    itemDetail,
    onDataBuyChange,
    onDataMemoChange,
    onDataHoldChange,
    setIsDirty,
    disableRequestButton,
    onModify,
    fromRequestDetail,
    isBulk,
    customForm,
    customFormInputs,
    dataHold,
  } = props;
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const theme = useTheme();
  const isFocused = useIsFocused();
  const { isVDBOrg } = useOrgSettingsSlice();
  const [selectedInvoiceCod, setSelectedInvoiceCod] = useState(
    () => getPaymentType(stockItemRequest?.payment_type) || "COD"
  );

  useEffect(() => {
    const raw = stockItemRequest?.payment_type;
    if (raw == null || raw === "") return;
    setSelectedInvoiceCod(getPaymentType(raw) || "COD");
  }, [stockItemRequest?.payment_type]);

  const userCurrency = useUserCurrency();
  const [ppcValidation, setPpcValidation] = useState("");
  const [tpValidation, setTpValidation] = useState("");
  const [selectedHoldHours, setSelectedHoldHours] = useState(
    dataHold?.hold_hours
  );

  const [discountPercent, setDiscountPercent] = useState(
    stockItemRequest?.offer_discount_percent || itemDetail?.discount_percent
  );

  const memoForm = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      memoComments: stockItemRequest?.comments ?? "",
      itemId: stockItemRequest?.id,
    },
  });

  const buyForm = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ctOffer: addComma(
        formatNumberWithPrecision(
          stockItemRequest?.offer_price_per_carat ??
            itemDetail?.price_per_carat ??
            "0",
          { precision: 0 }
        ) ?? "0"
      ),
      priceOffer: addComma(
        formatNumberWithPrecision(
          stockItemRequest?.offer_total_sales_price ??
            itemDetail?.total_sales_price ??
            "0",
          {
            precision:
              userInfo?.roundUpPrices() && !isMeleeItem(itemDetail) ? 0 : 2,
          }
        ) ?? "0"
      ),
      buyComments: stockItemRequest?.comments ?? "",
      payWithIn: stockItemRequest?.pay_within || 30,
      itemId: stockItemRequest?.id,
    },
  });
  const holdForm = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      itemId: stockItemRequest?.id,
    },
  });

  const isDirty =
    buyForm?.formState?.isDirty ||
    holdForm?.formState?.isDirty ||
    memoForm?.formState?.isDirty;

  useEffect(() => {
    setIsDirty?.(isDirty);
  }, [isDirty]);

  useEffect(() => {
    if (isFocused) {
      resetMemoForm();
      resetBuyForm();
    }
  }, [isFocused]);

  const resetMemoForm = useCallback(() => {
    memoForm.reset({
      memoComments: stockItemRequest?.comments ?? "",
    });
  }, [stockItemRequest, memoForm]);

  const resetBuyForm = useCallback(() => {
    buyForm.reset({
      ctOffer: addComma(
        formatNumberWithPrecision(
          stockItemRequest?.offer_price_per_carat ??
            itemDetail?.price_per_carat ??
            "0",
          { precision: 0 }
        ) ?? "0"
      ),
      priceOffer: addComma(
        formatNumberWithPrecision(
          stockItemRequest?.offer_total_sales_price ??
            itemDetail?.total_sales_price ??
            "0",
          {
            precision:
              userInfo?.roundUpPrices() && !isMeleeItem(itemDetail) ? 0 : 2,
          }
        ) ?? "0"
      ),
      buyComments: stockItemRequest?.comments ?? "",
      payWithIn: stockItemRequest?.pay_within || 30,
    });
  }, [stockItemRequest, buyForm]);

  const [disabledRequestButton, setDisabledRequestButton] = useState(
    // @ts-ignore
    !(buyForm.getValues("ctOffer") && buyForm.getValues("priceOffer")) ?? true
  );

  const handleCloseSheet = (hours: string) => {
    setSelectedHoldHours(hours);
    sendValues();
  };

  useEffect(() => {
    if (stockItemRequest?.request_type?.toLowerCase().includes("hold")) {
      setSelectedHoldHours(stockItemRequest?.hold_hours);
      holdForm.setValue("hold_hours", stockItemRequest?.hold_hours);
      holdForm.setValue("holdComments", stockItemRequest.comments);
    }
    sendValues();
  }, [stockItemRequest]);
  const reCalculatePrices = (key: string, value: any) => {
    value = key === "discount" && !value ? 0 : value;
    value = value.replaceAll(",", "");
    let tp;
    let ppc;

    const calculatedPrice = calculateItemPrices(itemDetail, key, value);
    switch (key) {
      case "ppc":
        if (
          calculatedPrice.totalPrice &&
          userInfo?.roundUpPrices() &&
          !isMeleeItem(itemDetail)
        ) {
          tp = calculatedPrice.totalPrice
            ? formatNumberWithPrecision(calculatedPrice.totalPrice, {
                precision: 0,
              })
            : "";
          ppc = !value ? "" : value;
        } else {
          tp = calculatedPrice.totalPrice
            ? formatNumberWithPrecision(calculatedPrice.totalPrice, {
                precision: 2,
              })
            : "";
          ppc = !value ? "" : value;
        }
        setDiscountPercent(
          calculatedPrice.discount
            ? formatNumberWithPrecision(calculatedPrice.discount, {
                precision: 2,
              })
            : ""
        );
        break;
      case "tp":
        ppc = calculatedPrice.ppc
          ? formatNumberWithPrecision(calculatedPrice.ppc, { precision: 0 })
          : "";
        tp = !value ? "" : value;
        setDiscountPercent(
          calculatedPrice.discount
            ? formatNumberWithPrecision(calculatedPrice.discount, {
                precision: 2,
              })
            : ""
        );
        break;
      case "discount":
        setDiscountPercent(!value ? "" : value);
        tp = calculatedPrice.totalPrice
          ? userInfo?.roundUpPrices() && !isMeleeItem(itemDetail)
            ? formatNumberWithPrecision(calculatedPrice.totalPrice, {
                precision: 0,
              })
            : formatNumberWithPrecision(calculatedPrice.totalPrice, {
                precision: 2,
              })
          : "";
        ppc = calculatedPrice.ppc
          ? formatNumberWithPrecision(calculatedPrice.ppc, { precision: 0 })
          : "";
        break;
    }
    buyForm.setValue("priceOffer", addComma(tp.replaceAll(",", "")));
    buyForm.setValue("ctOffer", addComma(ppc.replaceAll(",", "")));
    let newDisabledRequestButton = false;
    const isJewelry =
      itemDetail?.type === ProductCategory.JEWELRY ||
      itemDetail?.type === ProductCategory.LABGROWN_JEWELRY;

    const noPpc = !ppc || ppc === "0";

    const result = isVDBOrg && isJewelry ? false : noPpc;
    if (result) {
      setPpcValidation(`Please enter a price greater than ${userCurrency}0`);
      newDisabledRequestButton = true;
    } else {
      setPpcValidation("");
    }

    if (!tp || Number(tp) === 0) {
      setTpValidation(`Please enter a price greater than ${userCurrency}0`);
      newDisabledRequestButton = true;
    } else {
      setTpValidation("");
    }

    setDisabledRequestButton(newDisabledRequestButton);
    sendValues(newDisabledRequestButton);
  };

  const sendValues = (disableRequest: boolean = false) => {
    if (selectedOption === "buy") {
      const buyData = {
        ...buyForm.getValues(),
        discountPercent,
        selectedRadio: selectedInvoiceCod,
      };
      onDataBuyChange(buyData);
    } else if (selectedOption === "hold") {
      const holdData = {
        ...holdForm.getValues(),
      };

      onDataHoldChange(holdData);
    } else if (
      selectedOption === "memo" ||
      selectedOption === MemoStatus.Sold ||
      selectedOption === MemoStatus.Return
    ) {
      const memoData = {
        ...memoForm.getValues(),
      };
      onDataMemoChange(memoData);
    }
    disableRequestButton(disableRequest);
  };

  const toFixed = (key: any) => {
    let val = buyForm.getValues(key);
    let parsedVal = parseFloat((val + "").replaceAll(",", ""));

    if (!isNaN(parsedVal)) {
      if (key === "ctOffer") {
        buyForm.setValue(
          key,
          addComma(
            formatNumberWithPrecision(parsedVal, { precision: 0 }) ?? "0"
          )
        );
      } else {
        if (userInfo?.roundUpPrices() && !isMeleeItem(itemDetail))
          buyForm.setValue(
            key,
            addComma(
              formatNumberWithPrecision(parsedVal, { precision: 0 }) ?? "0"
            )
          );
        else
          buyForm.setValue(
            key,
            addComma(
              formatNumberWithPrecision(parsedVal, { precision: 2 }) ?? "0.00"
            )
          );
      }
    } else {
      buyForm.setValue(key, "");
    }
  };

  useEffect(() => {
    sendValues();
  }, [selectedInvoiceCod]);

  return (
    <>
      {HeaderComponent && <HeaderComponent />}
      {selectedOption === "memo" &&
        (canRequestAgain?.can_request_again === true ? (
          customForm ? (
            <DynamicForm
              form={customFormInputs}
              // onDataChange={handleDataChange}
              hideSubmitButton={true}
              fromRequestDetail={false} // onValidationReady={setCustomFormValidation}
            />
          ) : (
            <MemoForm
              memoForm={memoForm}
              t={t}
              sendValues={sendValues}
              theme={theme}
            />
          )
        ) : canRequestAgain?.can_request_again === false ? (
          <CanRequest ScreenText={canRequestAgain?.notice} />
        ) : null)}

      {selectedOption === "buy" &&
        (canRequestAgain?.can_request_again === true ? (
          customForm ? (
            <DynamicForm
              form={customFormInputs}
              hideSubmitButton={true}
              itemDetail={itemDetail}
              fromRequestDetail={false}
            />
          ) : (
            <BuyForm
              buyForm={buyForm}
              t={t}
              itemType={itemDetail?.type}
              userInfo={userInfo}
              reCalculatePrices={reCalculatePrices}
              selectedInvoiceCod={selectedInvoiceCod}
              setSelectedInvoiceCod={setSelectedInvoiceCod}
              onModify={onModify}
              selectedOption={selectedOption}
              sendValues={sendValues}
              ppcValidation={ppcValidation}
              tpValidation={tpValidation}
              disableRequestButton={disabledRequestButton}
              toFixed={toFixed}
              fromRequestDetail={fromRequestDetail}
              isBulk={isBulk}
              isItemMelee={isMeleeItem(itemDetail)}
            />
          )
        ) : canRequestAgain?.can_request_again === false ? (
          <CanRequest ScreenText={canRequestAgain?.notice} />
        ) : null)}
      {selectedOption === "hold" &&
        (canRequestAgain?.can_request_again === true ? (
          customForm ? (
            <DynamicForm
              form={customFormInputs}
              hideSubmitButton={true}
              fromRequestDetail={false}
            />
          ) : (
            <HoldForm
              holdForm={holdForm}
              onApply={(hours: string) => handleCloseSheet(hours)}
              itemDetail={itemDetail}
              selectedHoldHours={selectedHoldHours}
              sendValues={sendValues}
            />
          )
        ) : canRequestAgain?.can_request_again === false ? (
          <CanRequest ScreenText={canRequestAgain?.notice} />
        ) : null)}

      {selectedOption === MemoStatus.Sold &&
        (canRequestAgain?.can_request_again === true ? (
          customForm ? (
            <DynamicForm
              form={customFormInputs}
              // onDataChange={handleDataChange}
              hideSubmitButton={true}
              // onValidationReady={setCustomFormValidation}
            />
          ) : (
            <MemoForm
              memoForm={memoForm}
              t={t}
              sendValues={sendValues}
              theme={theme}
            />
          )
        ) : canRequestAgain?.can_request_again === false ? (
          <CanRequest ScreenText={canRequestAgain?.notice} />
        ) : null)}

      {selectedOption === MemoStatus.Return &&
        (canRequestAgain?.can_request_again === true ? (
          customForm ? (
            <DynamicForm
              form={customFormInputs}
              // onDataChange={handleDataChange}
              hideSubmitButton={true}
              // onValidationReady={setCustomFormValidation}
            />
          ) : (
            <MemoForm
              memoForm={memoForm}
              t={t}
              sendValues={sendValues}
              theme={theme}
            />
          )
        ) : canRequestAgain?.can_request_again === false ? (
          <CanRequest ScreenText={canRequestAgain?.notice} />
        ) : null)}
    </>
  );
};

export default BmhForm;
